import React, { useEffect, useState } from 'react';
import { Card, Button, Space, Tag, Typography, message } from 'antd';
import { SyncOutlined, InfoCircleOutlined } from '@ant-design/icons';
import { useHealthStore } from '@/stores/useHealthStore';
import { getCurrentUser } from '@/services/supabase';
import { getSyncStats, triggerManualSync, addSyncStatusListener } from '@/services/sync';
import type { SyncStatus } from '@/types';

const { Text } = Typography;

const SyncDiagnostics: React.FC = () => {
  const [, setSyncStatus] = useState<SyncStatus>('idle');
  const [userId, setUserId] = useState<string | null>(null);
  const [cloudStats, setCloudStats] = useState({
    healthRecords: 0,
    bpRecords: 0,
    bsRecords: 0
  });
  
  const { records, bpRecords, bsRecords } = useHealthStore();

  useEffect(() => {
    // 获取当前用户ID
    const getUserInfo = async () => {
      const user = await getCurrentUser();
      setUserId(user?.id || null);
    };
    getUserInfo();
    
    // 监听同步状态
    const unsubscribe = addSyncStatusListener((status) => {
      setSyncStatus(status);
    });
    
    return () => { unsubscribe(); };
  }, []);

  const checkCloudData = async () => {
    try {
      const stats = getSyncStats();
      if (!stats.currentUserId) {
        message.error('未登录，无法查询云端数据');
        return;
      }
      
      // 直接查询云端数据数量
      const token = JSON.parse(localStorage.getItem('sb-cunlsleuzmuthzwbbiwg-auth-token') || '{}').access_token;
      
      const [healthRes, bpRes, bsRes] = await Promise.all([
        fetch(`https://cunlsleuzmuthzwbbiwg.supabase.co/rest/v1/health_records_sync?select=id&user_id=eq.${stats.currentUserId}`, {
          headers: {
            'apikey': 'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6ImN1bmxzbGV1em11dGh6d2JiaXdnIiwicm9sZSI6ImFub24iLCJpYXQiOjE3NzI4NTI5MzAsImV4cCI6MjA4ODQyODkzMH0.7qcS5IRA3F_NKTKsCkdK7LX_qU-t6C1OdizKOV6QZjc',
            'Authorization': `Bearer ${token}`
          }
        }),
        fetch(`https://cunlsleuzmuthzwbbiwg.supabase.co/rest/v1/blood_pressure_sync?select=id&user_id=eq.${stats.currentUserId}`, {
          headers: {
            'apikey': 'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6ImN1bmxzbGV1em11dGh6d2JiaXdnIiwicm9sZSI6ImFub24iLCJpYXQiOjE3NzI4NTI5MzAsImV4cCI6MjA4ODQyODkzMH0.7qcS5IRA3F_NKTKsCkdK7LX_qU-t6C1OdizKOV6QZjc',
            'Authorization': `Bearer ${token}`
          }
        }),
        fetch(`https://cunlsleuzmuthzwbbiwg.supabase.co/rest/v1/blood_sugar_sync?select=id&user_id=eq.${stats.currentUserId}`, {
          headers: {
            'apikey': 'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6ImN1bmxzbGV1em11dGh6d2JiaXdnIiwicm9sZSI6ImFub24iLCJpYXQiOjE3NzI4NTI5MzAsImV4cCI6MjA4ODQyODkzMH0.7qcS5IRA3F_NKTKsCkdK7LX_qU-t6C1OdizKOV6QZjc',
            'Authorization': `Bearer ${token}`
          }
        })
      ]);
      
      const [healthData, bpData, bsData] = await Promise.all([
        healthRes.json(),
        bpRes.json(),
        bsRes.json()
      ]);
      
      setCloudStats({
        healthRecords: healthData.length || 0,
        bpRecords: bpData.length || 0,
        bsRecords: bsData.length || 0
      });
      
      message.success('云端数据统计完成');
    } catch (error) {
      console.error('Failed to check cloud data:', error);
      message.error('查询云端数据失败');
    }
  };

  const forceFullSync = async () => {
    // 重置最后同步时间，强制全量拉取
    localStorage.removeItem('health_last_sync_at');
    await triggerManualSync();
    message.success('已触发全量同步');
  };

  return (
    <Card title="同步诊断" style={{ marginBottom: 16 }}>
      <Space direction="vertical" style={{ width: '100%' }}>
        <div>
          <Text strong>当前用户ID: </Text>
          <Tag color="blue">{userId || '未登录'}</Tag>
        </div>
        
        <div>
          <Text strong>本地数据: </Text>
          <Tag>{records.length} 条记录</Tag>
          <Tag>{bpRecords.length} 条血压</Tag>
          <Tag>{bsRecords.length} 条血糖</Tag>
        </div>
        
        <div>
          <Text strong>云端数据: </Text>
          <Tag color="green">{cloudStats.healthRecords} 条记录</Tag>
          <Tag color="green">{cloudStats.bpRecords} 条血压</Tag>
          <Tag color="green">{cloudStats.bsRecords} 条血糖</Tag>
        </div>
        
        <Space>
          <Button icon={<InfoCircleOutlined />} onClick={checkCloudData}>
            检查云端数据
          </Button>
          <Button icon={<SyncOutlined />} onClick={forceFullSync} type="primary">
            强制全量同步
          </Button>
        </Space>
        
        <div style={{ marginTop: 8, padding: 8, background: '#f6ffed', borderRadius: 4 }}>
          <Text type="secondary" style={{ fontSize: 12 }}>
            💡 如果手机端同步失败，请确保所有设备使用相同的用户ID登录。
            当前用户ID应与其他设备一致。
          </Text>
        </div>
      </Space>
    </Card>
  );
};

export default SyncDiagnostics;
